import React, { useState, useContext, useEffect } from "react";
import Chart from "react-apexcharts";
import { Row, Col } from "react-bootstrap";
import { useFetch } from "../../../components/hooks/useFetch";
import { Context } from "../../../services/context";
import { Prefix } from "../../../services/prefix";
import { Arrow, ContentArrow } from "./readingSensorStyle";
import ChartEnergy from "../../components/charts/chartEnergy";
import TrakingRoute from "../trackingRoute";
import TankGauge from "../tankGauge";

const ReadingSensor = ({ sensor }) => {
  const { customerActionPlan, setCustomerActionPlan } = useContext(Context);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const urlSensor = `${Prefix.replace(/[\\"]/g, "")}/sismogran/map/items/profile/${sensor}/readings?page=${page}`;
  const listSensor = useFetch(urlSensor, 30000);

  useEffect(() => {
    if (listSensor.data || listSensor.error) setLoading(false);
  }, [listSensor.data, listSensor.error]);

  const HandlePrevious = () => {
    setLoading(true);
    setPage((prev) => prev + 1);
  };

  const HandleNext = () => {
    if (page > 1) {
      setLoading(true);
      setPage((prev) => prev - 1);
    }
  };

  if (!loading && (!listSensor.data?.models || Object.keys(listSensor.data.models).length === 0)) {
    return (
      <div style={{ textAlign: "center", padding: "40px", fontFamily: "Arial, sans-serif" }}>
        <h2 style={{ fontSize: "20px", color: "#333" }}>
          {listSensor.data?.message || "Nenhuma leitura encontrada"}
        </h2>
        <Row>
          <Col className="d-flex justify-content-end">
            {page > 1 && (
              <ContentArrow>
                <Arrow src="/media/icons/right-arrow.svg" alt="Right Arrow" onClick={HandleNext} />
              </ContentArrow>
            )}
          </Col>
        </Row>
      </div>
    );
  }

  const rawLogs = listSensor.data?.models?.logs || [];
  const logs = Array.isArray(rawLogs) ? rawLogs : Object.values(rawLogs);
  const orderedLogs = logs
    ? [...logs].sort((a, b) => new Date(a.epoch).getTime() - new Date(b.epoch).getTime())
    : [];

  const hasLogs = orderedLogs.length > 0;

  const colorsLine = ["#962624", "#834aad", "#834aad"];
  const widthLine = [1, 3, 1];

  const baseSeries = [
    {
      name: "Máximo",
      type: "line",
      data: hasLogs ? orderedLogs.map((log) => [new Date(log.epoch).getTime(), log.max]) : [],
    },
    {
      name: "Leitura",
      type: "line",
      data: hasLogs
        ? orderedLogs.map((log) => [new Date(log.epoch).getTime(), parseFloat(log.value).toFixed(2)])
        : [],
    },
    {
      name: "Mínimo",
      type: "line",
      data: hasLogs ? orderedLogs.map((log) => [new Date(log.epoch).getTime(), log.min]) : [],
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "40px" }}>
        <h2>Carregando dados do sensor...</h2>
      </div>
    );
  }

  const measureName = listSensor.data?.models?.profile_attribute?.unit_of_measure?.measure?.name?.toLowerCase();

  const renderColletor = () => {
    if (!measureName) return null;

    if (measureName.includes("consumo")) {
      return (
        <Col>
          <ChartEnergy
            energyData={listSensor.data.models.energy_chart}
            max={listSensor.data.models.max}
            total_consumed_kwh={listSensor.data.models.total_consumed_kwh}
            estimated_cost_brl={listSensor.data.models.estimated_cost_brl?.real_peak}
            height={290} // altura fixa
          />
        </Col>
      );
    } else if (measureName.includes("gps")) {
      return (
        <Col>
          <TrakingRoute
            locations={listSensor.data?.models.locations ?? ""}
            routeData={listSensor.data?.models.route}
            height={300} // altura fixa para manter layout uniforme
          />
        </Col>
      );
    } else if (measureName.includes("nível de líquido")) {
      return (
        <Col>
          <TankGauge
            value={listSensor.data?.models.value}
            max={listSensor.data?.models.max}
            min={listSensor.data?.models.min}
            size={1.2}
            height={300} // altura fixa
          />
        </Col>
      );
    } else {
      return (
        <Col>
          {hasLogs && (
            <Chart
              options={{
                chart: {
                  height: 360, // altura fixa
                  zoom: { type: "x", enabled: true, autoScaleYaxis: true },
                  events: {
                    markerClick: function (event, chartContext, { dataPointIndex }) {
                      setCustomerActionPlan(orderedLogs[dataPointIndex]);
                    },
                  },
                },
                stroke: { width: widthLine },
                colors: colorsLine,
                xaxis: {
                  type: "datetime",
                  labels: {
                    formatter: function (val) {
                      const d = new Date(val);
                      const now = new Date();

                      const hh = String(d.getHours()).padStart(2, "0");
                      const mm = String(d.getMinutes()).padStart(2, "0");
                      const dd = String(d.getDate()).padStart(2, "0");
                      const MM = String(d.getMonth() + 1).padStart(2, "0");

                      if (
                        d.getDate() === now.getDate() &&
                        d.getMonth() === now.getMonth() &&
                        d.getFullYear() === now.getFullYear()
                      ) {
                        return `${hh}:${mm}`;
                      }

                      return `${hh}:${mm} ${dd}/${MM}`;
                    },
                  },
                },
              }}
              series={baseSeries}
              width="100%"
              height={300} // altura fixa
            />
          )}
        </Col>
      );
    }
  };

  return (
    <>
      {renderColletor()}
      <Row>
        <Col>
          {hasLogs && (
            <ContentArrow>
              <Arrow src="/media/icons/lef-arrow.svg" alt="Left Arrow" onClick={HandlePrevious} />
            </ContentArrow>
          )}
        </Col>
        <Col className="d-flex justify-content-end">
          {page > 1 && (
            <ContentArrow>
              <Arrow src="/media/icons/right-arrow.svg" alt="Right Arrow" onClick={HandleNext} />
            </ContentArrow>
          )}
        </Col>
      </Row>
    </>
  );
};

export default ReadingSensor;